/**
 * AsyncSelectField — Single-select async search dropdown
 * integrated with react-hook-form via Controller.
 *
 * Debounced search → fetchFn → dropdown options.
 * DaisyUI dropdown + input pattern.
 */

import { FormField } from "@components/forms/FormField";
import { cn } from "@lib/utils/cn";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type Control, Controller } from "react-hook-form";

interface AsyncSelectFieldProps<T> {
  name: string;
  control: Control<any>;
  label: string;
  /** Fetch function called with search string */
  fetchFn: (search: string) => Promise<T[]>;
  /** Extract display label from option */
  getOptionLabel: (option: T) => string;
  /** Extract value from option */
  getOptionValue: (option: T) => number | string;
  /** Placeholder when no selection */
  placeholder?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Hint text */
  hint?: string;
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number;
}

export function AsyncSelectField<T>({
  name,
  control,
  label,
  fetchFn,
  getOptionLabel,
  getOptionValue,
  placeholder,
  required = false,
  disabled = false,
  hint,
  debounceMs = 300,
}: AsyncSelectFieldProps<T>) {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const doFetch = useCallback(
    async (query: string) => {
      setIsLoading(true);
      try {
        const result = await fetchFn(query);
        setOptions(result);
      } catch {
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFn],
  );

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      doFetch(search);
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [search, debounceMs, doFetch]);

  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: "Ce champ est requis" } : undefined}
      render={({ field, fieldState }) => (
        <FormField label={label} error={fieldState.error?.message} hint={hint}>
          <div ref={containerRef} className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setIsOpen(true);
                // Clear form value when user types (new search = no selection)
                if (field.value !== undefined && field.value !== null && field.value !== "") {
                  field.onChange(undefined);
                }
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={placeholder ?? "Rechercher..."}
              disabled={disabled}
              className={cn(
                "input input-bordered w-full pr-10 bg-base-200 border-base-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200",
                fieldState.error && "input-error",
              )}
            />

            {/* Loading spinner */}
            {isLoading && (
              <span className="loading loading-spinner loading-sm absolute right-3 top-1/2 -translate-y-1/2" />
            )}

            {/* Clear button */}
            {!isLoading && field.value != null && field.value !== "" && (
              <button
                type="button"
                onClick={() => {
                  field.onChange(undefined);
                  setSearch("");
                  setOptions([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content transition-colors duration-150"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Dropdown options */}
            {isOpen && !disabled && (
              <div className="absolute z-50 mt-1 w-full bg-base-200 border border-base-300/60 rounded-lg shadow-xl shadow-base-300/30 max-h-60 overflow-y-auto animate-dropdown-in">
                {options.length === 0 && !isLoading && (
                  <div className="px-4 py-2 text-sm text-base-content/50">Aucun résultat</div>
                )}
                {options.map((option) => (
                  <button
                    key={String(getOptionValue(option))}
                    type="button"
                    onClick={() => {
                      field.onChange(getOptionValue(option));
                      setSearch(getOptionLabel(option));
                      setIsOpen(false);
                    }}
                    className={cn(
                      "w-full px-4 py-2 text-left text-sm hover:bg-primary/10 transition-colors duration-150",
                      field.value === getOptionValue(option) &&
                        "bg-primary/10 font-medium border-l-2 border-l-primary",
                    )}
                  >
                    {getOptionLabel(option)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </FormField>
      )}
    />
  );
}

export default AsyncSelectField;
